import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckSquare, Clock, Calendar, Bell, LogOut, TrendingUp, AlertTriangle, ChevronRight, FileText,
  BookOpen, ExternalLink, HelpCircle, Plane, MessageSquare, StickyNote
} from 'lucide-react';
import OnboardingWizard from '../components/OnboardingWizard';
import TimeClockModal from '../components/TimeClockModal';
import GuidanceWidget from '../components/GuidanceWidget';
import NotificationPanel from '../components/NotificationPanel';
import HelpCenterModal from '../components/HelpCenterModal';
import HandbookModal from '../components/HandbookModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get auth headers
const getAuthHeaders = () => {
  const stored = sessionStorage.getItem('assistant');
  if (stored) {
    try {
      const assistant = JSON.parse(stored);
      return { 'x-assistant-session': assistant.id };
    } catch (e) {}
  }
  return {};
};

const AssistantDashboard = () => {
  const navigate = useNavigate();
  const [assistant, setAssistant] = useState(null);
  const [executives, setExecutives] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showTimeClock, setShowTimeClock] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [apiStatus, setApiStatus] = useState({ healthy: true, message: '' });
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  const [showHelpCenter, setShowHelpCenter] = useState(false);
  const [showHandbook, setShowHandbook] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const storedAssistant = sessionStorage.getItem('assistant');
    if (!storedAssistant) {
      navigate('/assistant-login');
      return;
    }

    try {
      const assistantData = JSON.parse(storedAssistant);
      setAssistant(assistantData);
      fetchDashboardData();
      checkOnboardingStatus();
    } catch (e) {
      console.error('Error parsing assistant data:', e);
      navigate('/assistant-login');
    }
    
    // Handle window resize for mobile detection
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [navigate]);

  // Fetch notification count
  useEffect(() => {
    const fetchNotificationCount = async () => {
      if (assistant?.id) {
        try {
          const res = await fetch(`${BACKEND_URL}/api/assistant/notifications`, {
            
            headers: { 'x-assistant-session': assistant.id },
          });
          if (res.ok) {
            const data = await res.json();
            setNotificationCount(data.unread_count || 0);
          }
        } catch (err) {
          console.error('Failed to fetch notification count:', err);
        }
      }
    };

    fetchNotificationCount();
    // Poll every minute
    const interval = setInterval(fetchNotificationCount, 60000);
    return () => clearInterval(interval);
  }, [assistant]);

  const checkOnboardingStatus = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/onboarding/status`, {
        headers: getAuthHeaders()
      });

      if (res.ok) {
        const data = await res.json();
        // Show onboarding if not completed
        if (!data.onboarding_completed) {
          setShowOnboarding(true);
        }
      }
      setOnboardingChecked(true);
    } catch (err) {
      console.error('Error checking onboarding status:', err);
      setOnboardingChecked(true);
    }
  };

  const fetchDashboardData = async () => {
    try {
      // Quick API health check
      const summaryRes = await fetch(`${BACKEND_URL}/api/assistant/dashboard/summary`, {
        headers: getAuthHeaders()
      });
      
      if (summaryRes.status === 401) {
        setApiStatus({ healthy: false, message: 'Session expired. Please log in again.' });
        setTimeout(() => navigate('/assistant-login'), 2000);
        return;
      }
      
      if (!summaryRes.ok) {
        setApiStatus({ healthy: false, message: `API Error ${summaryRes.status}: Unable to fetch dashboard data` });
      } else {
        setApiStatus({ healthy: true, message: '' });
      }
      
      if (summaryRes.ok) {
        const summaryData = await summaryRes.json();
        setSummary(summaryData.summary);
      }

      const execRes = await fetch(`${BACKEND_URL}/api/assistant/executives`, {
        headers: getAuthHeaders()
      });
      if (execRes.ok) {
        const execData = await execRes.json();
        setExecutives(execData.executives || []);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/assistant/logout`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      sessionStorage.removeItem('assistant');
      navigate('/assistant-login');
    } catch (error) {
      console.error('Logout error:', error);
      sessionStorage.removeItem('assistant');
      navigate('/assistant-login');
    }
  };

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
  };

  if (loading || !assistant) {
    return (
      <div style={{ minHeight: '100vh', background: '#F8F9FA', padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '32px',
            height: '32px',
            border: '3px solid #E5E7EB',
            borderTopColor: '#3B82F6',
            borderRadius: '50%',
            animation: 'spin 0.6s linear infinite',
            margin: '0 auto'
          }}></div>
          <p style={{ marginTop: '16px', color: '#6B7280', fontSize: '14px' }}>Loading...</p>
        </div>
      </div>
    );
  }

  const executiveNames = executives.map(e => e.full_name).join(', ') || 'None assigned';
  const hasActiveTasks = summary?.active_tasks > 0;
  const todayMeetings = summary?.today_meetings || 0;

  return (
    <>
      {/* Onboarding Wizard */}
      {showOnboarding && onboardingChecked && (
        <OnboardingWizard onComplete={handleOnboardingComplete} />
      )}
      
      {/* Main Dashboard */}
      <div className="page-transition" style={{ minHeight: '100vh', background: '#F8F9FA' }}>
        {/* Header */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: '#FFFFFF',
        borderBottom: '1px solid #E5E7EB'
      }}>
        <div style={{ 
          maxWidth: '1400px', 
          margin: '0 auto', 
          padding: isMobile ? '12px 16px' : '0 32px', 
          display: 'flex', 
          alignItems: isMobile ? 'flex-start' : 'center', 
          justifyContent: 'space-between', 
          minHeight: isMobile ? 'auto' : '64px',
          gap: isMobile ? '12px' : '0'
        }}>
          {/* Left - Mobile App Style Stacked Layout */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {isMobile ? (
              // Mobile: Clean stacked layout
              <>
                <h1 style={{ 
                  color: '#0A0A0A', 
                  fontSize: '16px', 
                  fontWeight: '600', 
                  margin: '0 0 4px 0', 
                  letterSpacing: '-0.01em',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {assistant.full_name}
                </h1>
                <div style={{ 
                  color: '#6B7280', 
                  fontSize: '12px', 
                  lineHeight: '1.4',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '4px 8px'
                }}>
                  <span>Executive: {executives[0]?.full_name || 'None'}</span>
                  {assistant.employee_id && (
                    <>
                      <span style={{ color: '#D1D5DB' }}>•</span>
                      <span>ID: {assistant.employee_id}</span>
                    </>
                  )}
                </div>
              </>
            ) : (
              // Desktop: Original layout
              <>
                <h1 style={{ color: '#0A0A0A', fontSize: '15px', fontWeight: '600', margin: 0, letterSpacing: '-0.01em' }}>
                  {assistant.full_name}
                </h1>
                <p style={{ color: '#6B7280', fontSize: '13px', margin: '3px 0 0 0' }}>
                  {executiveNames}
                  {assistant.employee_id && (
                    <span style={{ marginLeft: '12px', color: '#9CA3AF' }}>• {assistant.employee_id}</span>
                  )}
                </p>
              </>
            )}
          </div>

          {/* Right - Time Clock & Notifications */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
            {/* Time Clock Button */}
            <button
              onClick={() => setShowTimeClock(!showTimeClock)}
              style={{
                width: '36px',
                height: '36px',
                background: showTimeClock ? '#F9FAFB' : 'transparent',
                border: `1px solid ${showTimeClock ? '#D1D5DB' : '#E5E7EB'}`,
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'border-color 0.15s, background 0.15s'
              }}
              onMouseOver={(e) => {
                if (!showTimeClock) {
                  e.currentTarget.style.borderColor = '#D1D5DB';
                  e.currentTarget.style.background = '#F9FAFB';
                }
              }}
              onMouseOut={(e) => {
                if (!showTimeClock) {
                  e.currentTarget.style.borderColor = '#E5E7EB';
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              <Clock size={16} color="#6B7280" strokeWidth={2} />
            </button>

            {/* Settings Button */}
            <button
              onClick={() => navigate('/a-dashboard/settings')}
              style={{
                width: '36px',
                height: '36px',
                background: 'transparent',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'border-color 0.15s, background 0.15s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = '#D1D5DB';
                e.currentTarget.style.background = '#F9FAFB';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = '#E5E7EB';
                e.currentTarget.style.background = 'transparent';
              }}
              title="Settings"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="3"></circle>
                <path d="M12 1v6m0 6v6m8.66-16.66l-4.24 4.24m-8.48 8.48l-4.24 4.24M23 12h-6m-6 0H1m16.66 8.66l-4.24-4.24m-8.48-8.48l-4.24-4.24"></path>
              </svg>
            </button>

            {/* Notifications Button */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                style={{
                  position: 'relative',
                  width: '36px',
                  height: '36px',
                  background: showNotifications ? '#F9FAFB' : 'transparent',
                  border: `1px solid ${showNotifications ? '#D1D5DB' : '#E5E7EB'}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'border-color 0.15s, background 0.15s'
                }}
                onMouseOver={(e) => {
                  if (!showNotifications) {
                    e.currentTarget.style.borderColor = '#D1D5DB';
                    e.currentTarget.style.background = '#F9FAFB';
                  }
                }}
                onMouseOut={(e) => {
                  if (!showNotifications) {
                    e.currentTarget.style.borderColor = '#E5E7EB';
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                <Bell size={16} color="#6B7280" strokeWidth={2} />
                {notificationCount > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: '6px',
                    right: '6px',
                    minWidth: '16px',
                    height: '16px',
                    background: '#3B82F6',
                    color: '#FFFFFF',
                    borderRadius: '10px',
                    fontSize: '10px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 4px'
                  }}>
                    {notificationCount > 99 ? '99+' : notificationCount}
                  </span>
                )}
            </button>

          </div>

            <div style={{
              width: '36px',
              height: '36px',
              background: '#EFF6FF',
              border: '1px solid #DBEAFE',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#3B82F6',
              fontSize: '12px',
              fontWeight: '600',
              letterSpacing: '0.02em'
            }}>
              {assistant.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
            </div>

            <button
              onClick={handleLogout}
              style={{
                padding: '0 14px',
                height: '36px',
                background: 'transparent',
                color: '#6B7280',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'border-color 0.15s, background 0.15s, color 0.15s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = '#FCA5A5';
                e.currentTarget.style.background = '#FEF2F2';
                e.currentTarget.style.color = '#DC2626';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = '#E5E7EB';
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = '#6B7280';
              }}
            >
              <LogOut size={14} strokeWidth={2} />
              Sign out
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '32px' }}>
        {/* API Status Indicator */}
        {!apiStatus.healthy && (
          <div style={{
            background: '#FEF2F2',
            border: '1px solid #FCA5A5',
            borderRadius: '12px',
            padding: '16px 20px',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <AlertTriangle size={20} color="#DC2626" />
            <div>
              <p style={{ color: '#DC2626', fontSize: '14px', fontWeight: '600', margin: '0 0 4px 0' }}>
                API Connection Issue
              </p>
              <p style={{ color: '#991B1B', fontSize: '13px', margin: 0 }}>
                {apiStatus.message}
              </p>
            </div>
          </div>
        )}
        
        {/* Time Clock Widget */}
        {/* First 30 Days Guidance Widget */}
        {assistant && <GuidanceWidget assistantId={assistant.id} />}
        
        {/* VISUAL ANCHOR: Priority Today Section */}
        <div className="scroll-animate" style={{ marginBottom: '32px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <h2 style={{ color: '#0A0A0A', fontSize: '20px', fontWeight: '600', margin: 0, letterSpacing: '-0.02em' }}>
              Priority Today
            </h2>
            <span style={{ color: '#6B7280', fontSize: '13px', fontWeight: '500' }}>
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
            {/* Active Tasks Card */}
            <div style={{
              background: '#FFFFFF',
              border: '1px solid #FED7AA',
              borderLeft: '4px solid #F97316',
              borderRadius: '12px',
              padding: '28px',
              position: 'relative',
              transition: 'box-shadow 0.15s'
            }}
              onMouseOver={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(249,115,22,0.12)'}
              onMouseOut={(e) => e.currentTarget.style.boxShadow = 'none'}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <CheckSquare size={20} color="#F97316" strokeWidth={2} />
                    <h3 style={{ color: '#0A0A0A', fontSize: '15px', fontWeight: '600', margin: 0, letterSpacing: '-0.01em' }}>
                      Active Tasks
                    </h3>
                  </div>
                  <p style={{ color: '#6B7280', fontSize: '13px', margin: 0 }}>Pending in your to-do list</p>
                </div>
                <span style={{ fontSize: '48px', fontWeight: '600', color: '#F97316', letterSpacing: '-0.04em', lineHeight: '1' }}>
                  {summary?.active_tasks || 0}
                </span>
              </div>
              <button 
                onClick={() => navigate('/a-dashboard/todo')}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: '#FFF7ED',
                  color: '#F97316',
                  border: '1px solid #FED7AA',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  transition: 'background 0.15s'
                }}
                onMouseOver={(e) => e.target.style.background = '#FFEDD5'}
                onMouseOut={(e) => e.target.style.background = '#FFF7ED'}
              >
                View Tasks
                <ChevronRight size={14} strokeWidth={2} />
              </button>
            </div>

            {/* Today's Meetings Card */}
            <div style={{
              background: '#FFFFFF',
              border: '1px solid #DBEAFE',
              borderLeft: '4px solid #3B82F6',
              borderRadius: '12px',
              padding: '28px',
              transition: 'box-shadow 0.15s'
            }}
              onMouseOver={(e) => e.currentTarget.style.boxShadow = '0 4px 12px rgba(59,130,246,0.12)'}
              onMouseOut={(e) => e.currentTarget.style.boxShadow = 'none'}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                    <Calendar size={20} color="#3B82F6" strokeWidth={2} />
                    <h3 style={{ color: '#0A0A0A', fontSize: '15px', fontWeight: '600', margin: 0, letterSpacing: '-0.01em' }}>
                      Today's Meetings
                    </h3>
                  </div>
                  <p style={{ color: '#6B7280', fontSize: '13px', margin: 0 }}>
                    {todayMeetings === 0 ? "You're free today! 🎉" : "On your calendar"}
                  </p>
                </div>
                <span style={{ fontSize: '48px', fontWeight: '600', color: '#3B82F6', letterSpacing: '-0.04em', lineHeight: '1' }}>
                  {todayMeetings}
                </span>
              </div>
              <button 
                onClick={() => navigate('/a-dashboard/calendar')}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: '#EFF6FF',
                  color: '#3B82F6',
                  border: '1px solid #DBEAFE',
                  borderRadius: '8px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  transition: 'background 0.15s'
                }}
                onMouseOver={(e) => e.target.style.background = '#DBEAFE'}
                onMouseOut={(e) => e.target.style.background = '#EFF6FF'}
              >
                View Calendar
                <ChevronRight size={14} strokeWidth={2} />
              </button>
            </div>
          </div>
        </div>

        {/* Secondary Stats - Smaller, Receded */}
        <div style={{ marginBottom: '32px' }}>
          <h3 style={{ color: '#6B7280', fontSize: '13px', fontWeight: '500', margin: '0 0 12px 0', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
            Overview
          </h3>
          <div className="scroll-animate delay-1" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px' }}>
            {/* This Week */}
            <div style={{
              background: '#FFFFFF',
              border: '1px solid #E5E7EB',
              borderRadius: '10px',
              padding: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              transition: 'border-color 0.15s'
            }}
              onMouseOver={(e) => e.currentTarget.style.borderColor = '#D1D5DB'}
              onMouseOut={(e) => e.currentTarget.style.borderColor = '#E5E7EB'}
            >
              <div>
                <p style={{ color: '#6B7280', fontSize: '12px', margin: '0 0 2px 0' }}>This Week</p>
                <p style={{ color: '#0A0A0A', fontSize: '20px', fontWeight: '600', margin: 0, letterSpacing: '-0.02em' }}>
                  {summary?.week_tasks || 0}
                </p>
              </div>
              <TrendingUp size={18} color="#9CA3AF" strokeWidth={2} />
            </div>

            {/* Events */}
            <div style={{
              background: '#FFFFFF',
              border: '1px solid #E5E7EB',
              borderRadius: '10px',
              padding: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              transition: 'border-color 0.15s'
            }}
              onMouseOver={(e) => e.currentTarget.style.borderColor = '#D1D5DB'}
              onMouseOut={(e) => e.currentTarget.style.borderColor = '#E5E7EB'}
            >
              <div>
                <p style={{ color: '#6B7280', fontSize: '12px', margin: '0 0 2px 0' }}>Events (7d)</p>
                <p style={{ color: '#0A0A0A', fontSize: '20px', fontWeight: '600', margin: 0, letterSpacing: '-0.02em' }}>
                  {summary?.upcoming_events || 0}
                </p>
              </div>
              <Calendar size={18} color="#9CA3AF" strokeWidth={2} />
            </div>
          </div>
        </div>

        {/* Quick Access Tools */}
        <div className="scroll-animate delay-2" style={{
          background: '#FFFFFF',
          border: '1px solid #E5E7EB',
          borderRadius: '12px',
          padding: '32px'
        }}>
          <h2 style={{ color: '#0A0A0A', fontSize: '18px', fontWeight: '600', marginBottom: '20px', letterSpacing: '-0.02em' }}>
            Quick Access
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <button
              onClick={() => navigate('/a-dashboard/todo')}
              style={{
                padding: '20px',
                background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
                textAlign: 'left',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.2)',
                transition: 'transform 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <CheckSquare size={24} style={{ marginBottom: '8px' }} />
              <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>To-Do Center</div>
              <div style={{ fontSize: '13px', opacity: 0.9 }}>Manage your tasks</div>
            </button>

            <button
              onClick={() => navigate('/a-dashboard/executives')}
              style={{
                padding: '20px',
                background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
                textAlign: 'left',
                boxShadow: '0 4px 12px rgba(139, 92, 246, 0.2)',
                transition: 'transform 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <TrendingUp size={24} style={{ marginBottom: '8px' }} />
              <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>Executive Hub</div>
              <div style={{ fontSize: '13px', opacity: 0.9 }}>View executive details</div>
            </button>

            <button
              onClick={() => navigate('/a-dashboard/calendar')}
              style={{
                padding: '20px',
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
                textAlign: 'left',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.2)',
                transition: 'transform 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <Calendar size={24} style={{ marginBottom: '8px' }} />
              <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>Calendar</div>
              <div style={{ fontSize: '13px', opacity: 0.9 }}>Manage events</div>
            </button>

            <button
              onClick={() => navigate('/a-dashboard/notes')}
              style={{
                padding: '20px',
                background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
                textAlign: 'left',
                boxShadow: '0 4px 12px rgba(245, 158, 11, 0.2)',
                transition: 'transform 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <StickyNote size={24} style={{ marginBottom: '8px' }} />
              <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>Notes</div>
              <div style={{ fontSize: '13px', opacity: 0.9 }}>General & meeting notes</div>
            </button>

            <button
              onClick={() => navigate('/a-dashboard/exec-messages')}
              style={{
                padding: '20px',
                background: 'linear-gradient(135deg, #EC4899 0%, #DB2777 100%)',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
                textAlign: 'left',
                boxShadow: '0 4px 12px rgba(236, 72, 153, 0.2)',
                transition: 'transform 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <MessageSquare size={24} style={{ marginBottom: '8px' }} />
              <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>Messages</div>
              <div style={{ fontSize: '13px', opacity: 0.9 }}>Executive inbox</div>
            </button>

            <button
              onClick={() => navigate('/a-dashboard/meetings')}
              style={{
                padding: '20px',
                background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '10px',
                cursor: 'pointer',
                textAlign: 'left',
                boxShadow: '0 4px 12px rgba(139, 92, 246, 0.2)',
                transition: 'transform 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <Calendar size={24} style={{ marginBottom: '8px' }} />
              <div style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>Meetings</div>
              <div style={{ fontSize: '13px', opacity: 0.9 }}>Client requests</div>
            </button>
          </div>
        </div>

        {/* Resources & Tools */}
        <div className="scroll-animate delay-3" style={{
          background: '#FFFFFF',
          border: '1px solid #E5E7EB',
          borderRadius: '12px',
          padding: '32px',
          marginTop: '24px'
        }}>
          <h2 style={{ color: '#0A0A0A', fontSize: '18px', fontWeight: '600', marginBottom: '8px', letterSpacing: '-0.02em' }}>
            Resources & Tools
          </h2>
          <p style={{ color: '#6B7280', fontSize: '14px', marginBottom: '20px' }}>
            Quick links to helpful resources and documentation
          </p>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '12px' }}>
            <button
              onClick={() => setShowHandbook(true)}
              style={{
                padding: '16px',
                background: '#F9FAFB',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                width: '100%',
                textAlign: 'left',
                transition: 'border-color 0.15s, background 0.15s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = '#D1D5DB';
                e.currentTarget.style.background = '#F3F4F6';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = '#E5E7EB';
                e.currentTarget.style.background = '#F9FAFB';
              }}
            >
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                background: '#EFF6FF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <BookOpen size={20} color="#3B82F6" />
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: '500', margin: '0 0 2px 0' }}>
                  Company Handbook
                </p>
                <p style={{ color: '#6B7280', fontSize: '12px', margin: 0 }}>
                  Policies and procedures
                </p>
              </div>
              <ChevronRight size={16} color="#9CA3AF" />
            </button>

            <button
              onClick={() => setShowHelpCenter(true)}
              style={{
                padding: '16px',
                background: '#F9FAFB',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                width: '100%',
                textAlign: 'left',
                transition: 'border-color 0.15s, background 0.15s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = '#D1D5DB';
                e.currentTarget.style.background = '#F3F4F6';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = '#E5E7EB';
                e.currentTarget.style.background = '#F9FAFB';
              }}
            >
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                background: '#F0FDF4',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <HelpCircle size={20} color="#10B981" />
              </div>
              <div style={{ flex: 1 }}>
                <p style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: '500', margin: '0 0 2px 0' }}>
                  Help Center
                </p>
                <p style={{ color: '#6B7280', fontSize: '12px', margin: 0 }}>
                  FAQs and support
                </p>
              </div>
              <ChevronRight size={16} color="#9CA3AF" />
            </button>

            <button
              onClick={() => navigate('/a-dashboard/travel')}
              style={{
                padding: '16px',
                background: '#F9FAFB',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                transition: 'border-color 0.15s, background 0.15s'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.borderColor = '#D1D5DB';
                e.currentTarget.style.background = '#F3F4F6';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.borderColor = '#E5E7EB';
                e.currentTarget.style.background = '#F9FAFB';
              }}
            >
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '8px',
                background: '#DBEAFE',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Plane size={20} color="#2563EB" />
              </div>
              <div style={{ flex: 1, textAlign: 'left' }}>
                <p style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: '500', margin: '0 0 2px 0' }}>
                  Travel Binder
                </p>
                <p style={{ color: '#6B7280', fontSize: '12px', margin: 0 }}>
                  Executive travel items
                </p>
              </div>
              <ChevronRight size={16} color="#9CA3AF" />
            </button>

          </div>
        </div>
      </div>

      {/* Password functionality removed - Admin manages passwords */}
      
      {/* Time Clock Modal */}
      {showTimeClock && assistant && (
        <TimeClockModal 
          assistantId={assistant.id} 
          onClose={() => setShowTimeClock(false)} 
        />
      )}

      {/* Notification Panel */}
      {showNotifications && assistant && (
        <NotificationPanel
          assistantId={assistant.id}
          onClose={() => {
            setShowNotifications(false);
            // Refresh count after closing
            fetch(`${BACKEND_URL}/api/assistant/notifications`, {
              
              headers: { 'x-assistant-session': assistant.id },
            }).then(res => res.json()).then(data => {
              setNotificationCount(data.unread_count || 0);
            }).catch(err => console.error('Failed to refresh count:', err));
          }}
        />
      )}
      
      <HelpCenterModal
        isOpen={showHelpCenter}
        onClose={() => setShowHelpCenter(false)}
      />
      
      <HandbookModal
        isOpen={showHandbook}
        onClose={() => setShowHandbook(false)}
      />
      
      </div>
    </>
  );
};

export default AssistantDashboard;
